import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import Select from "../../components/ui/AppReactSelect";
import { useDispatch } from "react-redux";
import { Card, CircularProgress, Fade, Zoom } from "@material-ui/core";
import {
  Business,
  Email,
  Lock,
  Phone,
  LocationOn,
  Restaurant,
  Store,
  Category,
  CheckCircle,
  Visibility,
  VisibilityOff,
  Public,
  ArrowBack,
  ArrowForward,
} from "@material-ui/icons";

import { submitVendorRegistration } from "../../actions/vendorActions.js";
import LusukuLogo from "../../views/Components/LusukuLogo";
import { useGoogleMaps } from "../../GoogleMapsContext";
import LocationAutocomplete from "../LocationAutoComplete";
import usePlacesAutocomplete from "use-places-autocomplete";
import {
  MARKETS,
  SUPPORTED_COUNTRIES,
  DEFAULT_COUNTRY,
  getMarket,
} from "../../config/markets";
import CountryFlag from "../../components/CountryFlag.js";

// ─────────────────────────────────────────────────────────────────────────────
// MULTI-STEP VENDOR REGISTRATION (multi_national)
//
// Why a wizard: the single-page form showed 10+ fields at once and read as a
// bureaucratic wall — intimidating for first-time vendors. Three short steps
// (3-4 fields each) with a progress bar keeps perceived effort low.
//
// Why an explicit COUNTRY step-1 field: this form lives on lusuku.shop, which
// serves EVERY market — the hostname cannot tell us the vendor's operational
// country. The selection drives, in one place:
//   • the Google Places country restriction + map bias (no more hardcoded "ug")
//   • the phone dial-code prefix (submitted as full E.164)
//   • backend geo-bounds validation + the vendor's tenancy discriminator
// ─────────────────────────────────────────────────────────────────────────────

const businessTypeOptions = [
  { value: "RESTAURANT", label: "Restaurant" },
  { value: "RETAILER", label: "Retailer" },
  { value: "WHOLESALER", label: "Wholesaler" },
  { value: "HEALTHCARE", label: "Healthcare Provider" },
  { value: "EVENT_ORGANIZER", label: "Event Organizer" },
];

const deliveryVendorTypes = new Set([
  "RESTAURANT",
  "RETAILER",
  "WHOLESALER",
  "HEALTHCARE",
]);

const eventOrganizerCategories = [
  { value: "CINEMA", label: "Cinema" },
  { value: "CONCERT", label: "Concert Promoter" },
  { value: "SPORTS", label: "Sports Organizer" },
  { value: "THEATER", label: "Theater / Performing Arts" },
  { value: "FESTIVAL", label: "Festival Organizer" },
  { value: "CONFERENCE", label: "Conference / Business Events" },
  { value: "NIGHTLIFE", label: "Nightlife / Club Events" },
  { value: "EXHIBITION", label: "Exhibition / Trade Show" },
  { value: "COMMUNITY", label: "Community / Faith Events" },
  { value: "EDUCATION", label: "Education / Workshops" },
  { value: "GENERAL", label: "General Events" },
];

const countryOptions = SUPPORTED_COUNTRIES.map((code) => ({
  value: code,
  label: MARKETS[code].name,
}));

const validationSchema = Yup.object().shape({
  country: Yup.string().required("Please select your country of operation"),
  businessName: Yup.string().required("Business name is required"),
  operationMode: Yup.string().required("Vendor type is required"),
  organizerCategory: Yup.string().when("operationMode", {
    is: "EVENT_ORGANIZER",
    then: () => Yup.string().required("Please select an organizer type"),
    otherwise: () => Yup.string().notRequired(),
  }),
  email: Yup.string().email("Enter a valid email").required("Email is required"),
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Please confirm your password"),
  businessAddress: Yup.string().required("Business address is required"),
  businessPhone: Yup.string()
    .matches(/^[\d\s]{7,15}$/, "Enter a valid phone number (digits only)")
    .required("Business phone number is required"),
  typeOfGoods: Yup.string().when("operationMode", {
    is: (val) => ["RESTAURANT", "RETAILER", "WHOLESALER"].includes(val),
    then: () => Yup.string().required("This field is required"),
    otherwise: () => Yup.string().notRequired(),
  }),
  medicalLicenseNumber: Yup.string().when("operationMode", {
    is: "HEALTHCARE",
    then: () => Yup.string().required("Medical license number is required"),
    otherwise: () => Yup.string().notRequired(),
  }),
  specializations: Yup.string().when("operationMode", {
    is: "HEALTHCARE",
    then: () => Yup.string().required("Specializations are required"),
    otherwise: () => Yup.string().notRequired(),
  }),
});

const initialValues = {
  country: DEFAULT_COUNTRY,
  businessName: "",
  email: "",
  operationMode: "",
  organizerCategory: "",
  businessAddress: "",
  businessPhone: "", // local part; dial code is composed at submit
  typeOfGoods: "",
  password: "",
  confirmPassword: "",
  medicalLicenseNumber: "",
  certifications: "",
  specializations: "",
  typeOfServices: "",
  latitude: "",
  longitude: "",
  deliveryZoneRadius: 10,
};

// Which fields belong to which wizard step (drives per-step validation).
const STEP_FIELDS = [
  ["country", "operationMode", "organizerCategory", "businessName"],
  [
    "businessPhone",
    "businessAddress",
    "typeOfGoods",
    "medicalLicenseNumber",
    "specializations",
  ],
  ["email", "password", "confirmPassword"],
];

const STEP_LABELS = ["Business", "Location & Contact", "Account"];

// ─── Address autocomplete, scoped to the SELECTED market ────────────────────
// usePlacesAutocomplete captures its requestOptions on first render, so this
// lives in its own component and the parent remounts it via key={country}
// whenever the country changes. No hardcoded "ug"/Kampala anywhere.
function AddressAutocompleteField({
  market,
  mapsApiLoaded,
  defaultValue,
  onSelect,
  error,
}) {
  const {
    value,
    ready,
    suggestions: { data, loading: placesLoading, status: suggestionStatus } = { data: [] },
    setValue,
    clearSuggestions,
    status,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: market.geocodeCountry },
      location: {
        lat: () => market.mapCenter.lat,
        lng: () => market.mapCenter.lng,
      },
      radius: 200 * 1000,
      types: ["establishment"],
    },
    debounce: 100,
  });

  useEffect(() => {
    if (mapsApiLoaded) setValue("");
    // setValue is a stable setter from usePlacesAutocomplete; listing it
    // satisfies exhaustive-deps without a disable directive (the previous
    // directive referenced a rule the CRA build's ESLint couldn't resolve,
    // which hard-failed the production build).
  }, [mapsApiLoaded, setValue]);

  useEffect(() => {
    if (status === "ERROR") {
      console.error("[AddressAutocompleteField] suggestions error:", status);
    }
  }, [status]);

  return (
    <>
      <LocationAutocomplete
        value={value}
        setValue={setValue}
        ready={ready}
        data={data}
        clearSuggestions={clearSuggestions}
        mapsApiLoaded={mapsApiLoaded}
        loadingSuggestions={placesLoading} suggestionStatus={suggestionStatus}
        defaultValue={defaultValue}
        onLocationSelect={onSelect}
      />
      {error && <div className="invalid-feedback d-block">{error}</div>}
    </>
  );
}

const VendorRegistrationForm = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { mapsApiLoaded } = useGoogleMaps();
  const requestedType = new URLSearchParams(location.search)
    .get("type")
    ?.toUpperCase();
  const lockedOperationMode = businessTypeOptions.some(
    (option) => option.value === requestedType
  )
    ? requestedType
    : "";
  const formInitialValues = {
    ...initialValues,
    operationMode: lockedOperationMode,
  };

  const [step, setStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleFormSubmit = (values, { setSubmitting, resetForm, setFieldError }) => {
    const market = getMarket(values.country);
    // Compose full E.164 from the market dial code + local digits ("0771…" → "+256771…").
    const localDigits = String(values.businessPhone).replace(/\D/g, "").replace(/^0+/, "");
    const payload = {
      ...values,
      businessPhone: `+${market.dialCode}${localDigits}`,
      country: values.country,
    };

    dispatch(submitVendorRegistration(payload))
      .then(() => {
        setSubmitting(false);
        resetForm({ values: formInitialValues });
        setStep(0);
        if (location.state?.returnTo) {
          history.push(location.state.returnTo);
        }
      })
      .catch((error) => {
        const errorField = error.response?.data?.field;
        const errorMessage =
          error.response?.data?.message || "Registration failed. Please try again.";
        if (errorField && setFieldError) setFieldError(errorField, errorMessage);
        // Jump back to the step that owns the failing field so the vendor sees it.
        if (errorField) {
          const ownerStep = STEP_FIELDS.findIndex((fields) =>
            fields.includes(errorField)
          );
          if (ownerStep >= 0) setStep(ownerStep);
        }
        setSubmitting(false);
      });
  };

  // Validate ONLY the current step's fields; advance when they're clean.
  const handleContinue = (values, setFieldError, setTouched) => {
    const fields = STEP_FIELDS[step];
    setTouched(
      fields.reduce((acc, f) => ({ ...acc, [f]: true }), {}),
      false
    );
    try {
      validationSchema.validateSync(values, { abortEarly: false });
      setStep((s) => Math.min(s + 1, STEP_FIELDS.length - 1));
    } catch (errors) {
      if (errors instanceof Yup.ValidationError) {
        const stepErrors = errors.inner.filter((e) => fields.includes(e.path));
        if (stepErrors.length === 0) {
          setStep((s) => Math.min(s + 1, STEP_FIELDS.length - 1));
        } else {
          stepErrors.forEach((e) => setFieldError(e.path, e.message));
        }
      }
    }
  };

  const handleFinalSubmit = (values, formikBag, setFieldError, setTouched) => {
    setTouched(
      STEP_FIELDS[2].reduce((acc, f) => ({ ...acc, [f]: true }), {}),
      false
    );
    try {
      validationSchema.validateSync(values, { abortEarly: false });
      formikBag.setSubmitting(true);
      handleFormSubmit(values, formikBag);
    } catch (errors) {
      formikBag.setSubmitting(false);
      if (errors instanceof Yup.ValidationError) {
        errors.inner.forEach((e) => setFieldError(e.path, e.message));
        // If the error belongs to an earlier step, send the vendor back to it.
        const firstBadStep = STEP_FIELDS.findIndex((fields) =>
          errors.inner.some((e) => fields.includes(e.path))
        );
        if (firstBadStep >= 0 && firstBadStep < step) setStep(firstBadStep);
      }
    }
  };

  const renderError = (errors, touched, name) =>
    errors[name] && touched[name] ? (
      <div className="invalid-feedback d-block">{errors[name]}</div>
    ) : null;

  const fieldClass = (errors, touched, name) =>
    `form-control ${errors[name] && touched[name] ? "is-invalid" : ""}`;

  return (
    <div className={classes.container}>
      <LusukuLogo />
      <div className="container py-2">
        <div className="row justify-content-center">
          <div className="col-lg-6 col-md-8">
            <Zoom in={true} timeout={400}>
              <Card className={classes.formCard}>
                <div className={classes.cardHeader}>
                  <h3 className={classes.headerTitle}>
                    {lockedOperationMode === "HEALTHCARE"
                      ? "Create Healthcare Vendor"
                      : lockedOperationMode === "EVENT_ORGANIZER"
                      ? "Create Event Organizer"
                      : "Become a Lusuku Vendor"}
                  </h3>
                  <p className={classes.headerSubtitle}>
                    {lockedOperationMode === "HEALTHCARE"
                      ? "Set up the provider, location, and account details"
                      : lockedOperationMode === "EVENT_ORGANIZER"
                      ? "Set up the organizer, contact, and account details"
                      : "3 quick steps — takes about 2 minutes"}
                  </p>
                </div>

                {/* Progress indicator */}
                <div className={classes.progressWrap}>
                  {STEP_LABELS.map((label, i) => (
                    <div key={label} className={classes.progressStep}>
                      <div
                        className={`${classes.progressDot} ${
                          i < step
                            ? classes.dotDone
                            : i === step
                            ? classes.dotActive
                            : ""
                        }`}
                      >
                        {i < step ? <CheckCircle style={{ fontSize: 18 }} /> : i + 1}
                      </div>
                      <span
                        className={`${classes.progressLabel} ${
                          i === step ? classes.progressLabelActive : ""
                        }`}
                      >
                        {label}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="card-body px-4 pb-4 pt-2">
                  <Formik
                    initialValues={formInitialValues}
                    validationSchema={validationSchema}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      isSubmitting,
                      setFieldValue,
                      setFieldError,
                      setTouched,
                      setSubmitting,
                      resetForm,
                    }) => {
                      const market = getMarket(values.country);
                      return (
                        <Form
                          onSubmit={(e) => {
                            e.preventDefault();
                            if (step < STEP_FIELDS.length - 1) {
                              handleContinue(values, setFieldError, setTouched);
                            } else {
                              handleFinalSubmit(
                                values,
                                { setSubmitting, resetForm, setFieldError },
                                setFieldError,
                                setTouched
                              );
                            }
                          }}
                        >
                          {/* ── STEP 1 · Business ── */}
                          {step === 0 && (
                            <Fade in={true} timeout={400}>
                              <div>
                                <div className="form-group mb-4">
                                  <label className={classes.fieldLabel}>
                                    <Public className={classes.labelIcon} />
                                    Country of Operation
                                  </label>
                                  <Select
                                    classNamePrefix="react-select"
                                    options={countryOptions}
                                    formatOptionLabel={(option) => (
                                      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                                        <CountryFlag country={option.value} width={22} decorative />
                                        <span>{option.label}</span>
                                      </span>
                                    )}
                                    value={countryOptions.find(
                                      (o) => o.value === values.country
                                    )}
                                    onChange={(opt) => {
                                      const next = opt ? opt.value : DEFAULT_COUNTRY;
                                      setFieldValue("country", next);
                                      // Address is market-scoped — clear it on switch.
                                      setFieldValue("businessAddress", "");
                                      setFieldValue("latitude", "");
                                      setFieldValue("longitude", "");
                                    }}
                                  />
                                  {renderError(errors, touched, "country")}
                                </div>

                                <div className="form-group mb-4">
                                  <label className={classes.fieldLabel}>
                                    <Category className={classes.labelIcon} />
                                    Business Type
                                  </label>
                                  <Field name="operationMode">
                                    {({ field, form }) => (
                                      <Select
                                        classNamePrefix="react-select"
                                        options={businessTypeOptions}
                                        isDisabled={Boolean(lockedOperationMode)}
                                        placeholder="Select your business type..."
                                        value={
                                          businessTypeOptions.find(
                                            (o) => o.value === field.value
                                          ) || null
                                        }
                                        onChange={(opt) =>
                                          form.setFieldValue(
                                            "operationMode",
                                            opt ? opt.value : ""
                                          )
                                        }
                                      />
                                    )}
                                  </Field>
                                  {renderError(errors, touched, "operationMode")}
                                </div>

                                <div className="form-group mb-2">
                                  <label className={classes.fieldLabel}>
                                    {values.operationMode === "RESTAURANT" ? (
                                      <Restaurant className={classes.labelIcon} />
                                    ) : (
                                      <Business className={classes.labelIcon} />
                                    )}
                                    {values.operationMode === "RESTAURANT"
                                      ? "Restaurant Name"
                                      : values.operationMode === "HEALTHCARE"
                                      ? "Healthcare Provider Name"
                                      : values.operationMode === "EVENT_ORGANIZER"
                                      ? "Organizer Name"
                                      : "Business Name"}
                                  </label>
                                  <Field
                                    type="text"
                                    className={fieldClass(errors, touched, "businessName")}
                                    name="businessName"
                                    placeholder="Enter business name"
                                  />
                                  {renderError(errors, touched, "businessName")}
                                </div>

                                {values.operationMode === "EVENT_ORGANIZER" && (
                                  <div className="form-group mt-4 mb-2">
                                    <label className={classes.fieldLabel}>
                                      <Category className={classes.labelIcon} />
                                      Organizer Type
                                    </label>
                                    <Select
                                      classNamePrefix="react-select"
                                      options={eventOrganizerCategories}
                                      placeholder="Select the events you organize..."
                                      value={
                                        eventOrganizerCategories.find(
                                          (option) => option.value === values.organizerCategory
                                        ) || null
                                      }
                                      onChange={(option) =>
                                        setFieldValue(
                                          "organizerCategory",
                                          option ? option.value : ""
                                        )
                                      }
                                    />
                                    {renderError(errors, touched, "organizerCategory")}
                                  </div>
                                )}
                              </div>
                            </Fade>
                          )}

                          {/* ── STEP 2 · Location & Contact ── */}
                          {step === 1 && (
                            <Fade in={true} timeout={400}>
                              <div>
                                <div className="form-group mb-4">
                                  <label className={classes.fieldLabel}>
                                    <Phone className={classes.labelIcon} />
                                    Business Phone
                                  </label>
                                  <div className="input-group">
                                    <span className={`input-group-text ${classes.dialCode}`}>
                                      <CountryFlag country={values.country} width={22} decorative />
                                      <span style={{ marginLeft: 6 }}>+{market.dialCode}</span>
                                    </span>
                                    <Field
                                      type="tel"
                                      className={fieldClass(errors, touched, "businessPhone")}
                                      name="businessPhone"
                                      placeholder="7XX XXX XXX"
                                    />
                                  </div>
                                  {renderError(errors, touched, "businessPhone")}
                                </div>

                                <div className="form-group mb-4">
                                  <label className={classes.fieldLabel}>
                                    <LocationOn className={classes.labelIcon} />
                                    Business Address ({market.name})
                                  </label>
                                  {/* key={country} remounts the autocomplete with the
                                      right market restriction when country changes */}
                                  <AddressAutocompleteField
                                    key={values.country}
                                    market={market}
                                    mapsApiLoaded={mapsApiLoaded}
                                    defaultValue={values.businessAddress}
                                    onSelect={({ address, latitude, longitude }) => {
                                      setFieldValue("businessAddress", address);
                                      setFieldValue("latitude", latitude);
                                      setFieldValue("longitude", longitude);
                                    }}
                                    error={
                                      touched.businessAddress
                                        ? errors.businessAddress
                                        : null
                                    }
                                  />
                                </div>

                                {["RESTAURANT", "RETAILER", "WHOLESALER"].includes(
                                  values.operationMode
                                ) && (
                                  <div className="form-group mb-4">
                                    <label className={classes.fieldLabel}>
                                      <Category className={classes.labelIcon} />
                                      {values.operationMode === "RESTAURANT"
                                        ? "Cuisine Specialty"
                                        : "Type of Goods"}
                                    </label>
                                    <Field
                                      type="text"
                                      className={fieldClass(errors, touched, "typeOfGoods")}
                                      name="typeOfGoods"
                                      placeholder={
                                        values.operationMode === "RESTAURANT"
                                          ? "e.g. Local dishes, Fast food..."
                                          : "e.g. Groceries, Electronics..."
                                      }
                                    />
                                    {renderError(errors, touched, "typeOfGoods")}
                                  </div>
                                )}

                                {deliveryVendorTypes.has(values.operationMode) &&
                                  values.operationMode !== "RESTAURANT" && (
                                  <div className="form-group mb-4">
                                    <label className={classes.fieldLabel}>
                                      <LocationOn className={classes.labelIcon} />
                                      Delivery Zone Radius (km)
                                    </label>
                                    <Field
                                      type="number"
                                      className="form-control"
                                      name="deliveryZoneRadius"
                                      min="1"
                                      placeholder="e.g. 10"
                                    />
                                  </div>
                                )}

                                {values.operationMode === "HEALTHCARE" && (
                                  <>
                                    <div className="form-group mb-4">
                                      <label className={classes.fieldLabel}>
                                        <Category className={classes.labelIcon} />
                                        Medical License Number
                                      </label>
                                      <Field
                                        type="text"
                                        className={fieldClass(
                                          errors,
                                          touched,
                                          "medicalLicenseNumber"
                                        )}
                                        name="medicalLicenseNumber"
                                        placeholder="Enter your medical license number..."
                                      />
                                      {renderError(errors, touched, "medicalLicenseNumber")}
                                    </div>
                                    <div className="form-group mb-4">
                                      <label className={classes.fieldLabel}>
                                        <Category className={classes.labelIcon} />
                                        Specializations
                                      </label>
                                      <Field
                                        type="text"
                                        className={fieldClass(errors, touched, "specializations")}
                                        name="specializations"
                                        placeholder="Comma-separated, e.g. Pharmacy, Lab tests"
                                      />
                                      {renderError(errors, touched, "specializations")}
                                    </div>
                                    <div className="form-group mb-4">
                                      <label className={classes.fieldLabel}>
                                        <Category className={classes.labelIcon} />
                                        Certifications{" "}
                                        <span className={classes.optionalTag}>optional</span>
                                      </label>
                                      <Field
                                        type="text"
                                        className="form-control"
                                        name="certifications"
                                        placeholder="Comma-separated"
                                      />
                                    </div>
                                    <div className="form-group mb-2">
                                      <label className={classes.fieldLabel}>
                                        <Category className={classes.labelIcon} />
                                        Type of Services{" "}
                                        <span className={classes.optionalTag}>optional</span>
                                      </label>
                                      <Field
                                        type="text"
                                        className="form-control"
                                        name="typeOfServices"
                                        placeholder="e.g. Consultations, Delivery of prescriptions"
                                      />
                                    </div>
                                  </>
                                )}
                              </div>
                            </Fade>
                          )}

                          {/* ── STEP 3 · Account ── */}
                          {step === 2 && (
                            <Fade in={true} timeout={400}>
                              <div>
                                <div className="form-group mb-4">
                                  <label className={classes.fieldLabel}>
                                    <Email className={classes.labelIcon} />
                                    Email Address
                                  </label>
                                  <Field
                                    type="email"
                                    className={fieldClass(errors, touched, "email")}
                                    name="email"
                                    placeholder="Enter business email"
                                  />
                                  {renderError(errors, touched, "email")}
                                </div>

                                <div className="form-group mb-4">
                                  <label className={classes.fieldLabel}>
                                    <Lock className={classes.labelIcon} />
                                    Password
                                  </label>
                                  <div className={classes.passwordWrapper}>
                                    <Field
                                      type={showPassword ? "text" : "password"}
                                      className={fieldClass(errors, touched, "password")}
                                      name="password"
                                      placeholder="At least 8 characters"
                                    />
                                    <span
                                      className={classes.eyeIcon}
                                      onClick={() => setShowPassword(!showPassword)}
                                      style={{ cursor: "pointer" }}
                                    >
                                      {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </span>
                                  </div>
                                  {renderError(errors, touched, "password")}
                                </div>

                                <div className="form-group mb-2">
                                  <label className={classes.fieldLabel}>
                                    <Lock className={classes.labelIcon} />
                                    Confirm Password
                                  </label>
                                  <div className={classes.passwordWrapper}>
                                    <Field
                                      type={showConfirmPassword ? "text" : "password"}
                                      className={fieldClass(errors, touched, "confirmPassword")}
                                      name="confirmPassword"
                                      placeholder="Re-enter password"
                                    />
                                    <span
                                      className={classes.eyeIcon}
                                      onClick={() =>
                                        setShowConfirmPassword(!showConfirmPassword)
                                      }
                                      style={{ cursor: "pointer" }}
                                    >
                                      {showConfirmPassword ? (
                                        <VisibilityOff />
                                      ) : (
                                        <Visibility />
                                      )}
                                    </span>
                                  </div>
                                  {renderError(errors, touched, "confirmPassword")}
                                </div>
                              </div>
                            </Fade>
                          )}

                          {/* ── Navigation ── */}
                          <div className="d-flex gap-2 mt-4">
                            {step > 0 && (
                              <button
                                type="button"
                                className={`btn ${classes.backBtn}`}
                                onClick={() => setStep((s) => Math.max(s - 1, 0))}
                                disabled={isSubmitting}
                              >
                                <ArrowBack style={{ fontSize: 18, marginRight: 6 }} />
                                Back
                              </button>
                            )}
                            <button
                              type="submit"
                              className={`btn flex-grow-1 ${
                                isSubmitting ? classes.disabledBtn : classes.enabledBtn
                              }`}
                              disabled={isSubmitting}
                              style={{
                                color: isSubmitting ? "#6c957d" : "#fff",
                                opacity: isSubmitting ? "0.8" : "1",
                              }}
                            >
                              {isSubmitting ? (
                                <div className="d-flex align-items-center justify-content-center">
                                  <span className="me-2">Creating Account...</span>
                                  <CircularProgress
                                    style={{ color: "#84BF04" }}
                                    size="1.2rem"
                                  />
                                </div>
                              ) : step < STEP_FIELDS.length - 1 ? (
                                <>
                                  Continue
                                  <ArrowForward
                                    style={{ fontSize: 18, marginLeft: 6 }}
                                  />
                                </>
                              ) : (
                                <>
                                  <CheckCircle style={{ marginRight: 8, fontSize: 20 }} />
                                  Create Vendor Account
                                </>
                              )}
                            </button>
                          </div>
                        </Form>
                      );
                    }}
                  </Formik>
                </div>
              </Card>
            </Zoom>
          </div>
        </div>
      </div>
    </div>
  );
};

const useStyles = makeStyles(() => ({
  container: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    /* Just enough top padding to clear the fixed logo AppBar (~101px) with a
       small breathing gap — the card now sits near the top instead of being
       pushed halfway down the page. */
    paddingTop: "110px",
    background: "linear-gradient(135deg, #f5f7fa 0%, #e8f5e9 100%)",
  },
  formCard: {
    boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
    borderRadius: "20px",
    border: "none",
    overflow: "hidden",
  },
  cardHeader: {
    background: "linear-gradient(135deg, #172601 0%, #2E5902 100%)",
    padding: "1.75rem 2rem 1.5rem",
    textAlign: "center",
    color: "#fff",
  },
  headerTitle: {
    fontSize: "1.5rem",
    fontWeight: 700,
    marginBottom: "0.25rem",
  },
  headerSubtitle: {
    fontSize: "0.9rem",
    opacity: 0.85,
    margin: 0,
  },
  progressWrap: {
    display: "flex",
    justifyContent: "center",
    gap: "2.5rem",
    padding: "1.25rem 1rem 0.5rem",
  },
  progressStep: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "0.35rem",
  },
  progressDot: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.85rem",
    fontWeight: 600,
    backgroundColor: "#e9ecef",
    color: "#6c757d",
    transition: "all 0.25s ease",
  },
  dotActive: {
    backgroundColor: "#2E5902",
    color: "#fff",
    boxShadow: "0 0 0 4px rgba(132,191,4,0.25)",
  },
  dotDone: {
    backgroundColor: "#84BF04",
    color: "#fff",
  },
  progressLabel: {
    fontSize: "0.75rem",
    color: "#adb5bd",
    fontWeight: 500,
  },
  progressLabelActive: {
    color: "#2E5902",
    fontWeight: 700,
  },
  fieldLabel: {
    fontSize: "0.95rem",
    fontWeight: 600,
    color: "#495057",
    marginBottom: "0.5rem",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  labelIcon: {
    fontSize: "1.1rem",
    color: "#84BF04",
  },
  optionalTag: {
    fontSize: "0.75rem",
    fontWeight: 400,
    color: "#adb5bd",
    marginLeft: 4,
  },
  dialCode: {
    fontWeight: 600,
    backgroundColor: "#f8f9fa",
    whiteSpace: "nowrap",
  },
  enabledBtn: {
    backgroundColor: "#172601",
    borderColor: "#84BF04",
    padding: "12px",
    fontSize: "1.05rem",
    fontWeight: 600,
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      backgroundColor: "#2E5902",
    },
  },
  disabledBtn: {
    backgroundColor: "#6c757d",
    borderColor: "#6c757d",
    padding: "12px",
    fontSize: "1.05rem",
    fontWeight: 600,
    cursor: "not-allowed",
    borderRadius: "10px",
    opacity: 0.7,
  },
  backBtn: {
    border: "1px solid #ced4da",
    color: "#495057",
    padding: "12px 20px",
    fontWeight: 600,
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
  },
  passwordWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  eyeIcon: {
    position: "absolute",
    right: "12px",
    color: "#84BF04",
    display: "flex",
    alignItems: "center",
    zIndex: 1,
    "&:hover": {
      color: "#2E5902",
    },
  },
}));

export default VendorRegistrationForm;
